import _ from 'lodash'
import { jsonCloneDeep } from '../helpers/utils'
// import { compare as jsonDiff } from 'fast-json-patch'
import moment from 'moment'

const defaultHandler = {
  get: function (prop) {
    return this[prop]
  },
  set: function (prop, value) {
    this[prop] = value
  }
}

export interface IEntityProxyOptions {
  /** Enable compression for execution-specific event logs */
  enableEventLogCompression?: boolean

  /**
   * Use copy-on-write strategy to defer setting prevContent/origContent
   * until the entity is modified. Helps reduce unnecessary memory usage.
   */
  copyOnWrite?: boolean
}

export class EntityProxy {

  private _handler: any
  private _content: any
  private _prevContent: any
  /**
   * original, unmodified value from the server. User to generate
   * JSON patch for entity "PATCH" update method. The UI code should
   * never touch this field.
   */
  private _origContent: any
  protected _options: any

  constructor(target, handler = defaultHandler, options:IEntityProxyOptions={}) {
    this._options = options
    this._content = target
    this._handler = handler
    this.syncOtherContents()
  }

  public get content() {
    return this._content
  }

  public set content(content) {
    throw new Error('Cannot update content directly. Use setContent')
  }

  public get isDirty() {
    // DEV - uncomment this line to quickly see what's dirtying your entity, usually just a little rogue formula.
    // const diff = jsonDiff(this._prevContent, this.content)
    return !_.isNil(this._prevContent) && !_.isEqual(this._prevContent, this.content)
  }

  public get prevContent() {
    return this._prevContent ?? this.content
  }

  public set prevContent(content) {
    throw new Error('Cannot update content directly. Use setPrevContent')
  }

  public get origContent() {
    return this._origContent ?? this.content
  }

  public set origContent(content) {
    throw new Error('Cannot update content directly. Use setOrigContent')
  }

  public get(path, defaultValue?) {
    if (!path) {
      return defaultValue
    }
    // TODO: consider alternatives, like another Proxy to intercept prevContent root directly, if overhead is acceptable.
    if (typeof path === 'string' && path.startsWith('prevContent.')) {
      return _.get(this.prevContent, path.slice('prevContent.'.length), defaultValue)
    }
    return _.get(this.content, path, defaultValue)
  }

  public set(path, value) {
    this.initializeOtherContents()
    if (_.isNil(value)) {
      _.unset(this._content, path)
    } else {
      _.set(this._content, path, value)
    }
    return this._content
  }

  /**
   * Sets a value in both content and prevContent.
   */
  public commit(path, value = undefined) {
    this.initializeOtherContents()
    _.set(this._content, path, value)
    _.set(this._prevContent, path, jsonCloneDeep(value))
  }

  public rollback() {
    this.setContent(jsonCloneDeep(this._prevContent))
  }

  public setContent(content) {
    if (_.isNil(content)) {
      return
    }
    this.initializeOtherContents()
    this._content = content
    this.registerOwnProperties()
  }

  public setPrevContent(content) {
    if (global.MEMORY_LIMITED) {
      return
    }

    this._prevContent = jsonCloneDeep(content)
  }

  public setOrigContent(content) {
    if (global.MEMORY_LIMITED) {
      return
    }

    this._origContent = jsonCloneDeep(content)
  }

  public updateOrigContent(content) {
    if (global.MEMORY_LIMITED) {
      return
    }
    const currDate = this.origContent.modifiedDate
    const otherDate = content?.modifiedDate

    // only update if newer
    if (moment(otherDate)?.isAfter(moment(currDate))) {
      this.setOrigContent(content)
    }
  }

  public setAllContents(content) {
    this.setContent(content)
    this.syncOtherContents()
  }

  public initializeOtherContents() {
    // Use copy-on-write to defer setting orig/prev content until first mutation.
    // This avoids redundant memory usage when content is initially identical.
    const content = this.content;

    if (_.isNil(this._prevContent)) {
      this.setPrevContent(content);
    }

    if (_.isNil(this._origContent)) {
      this.setOrigContent(content);
    }
  }

  protected registerOwnProperties() {
    // Clone direct properties (i.e., not part of a prototype).
    const target = this._content
    const propertyNames = Object.getOwnPropertyNames(target)
    propertyNames.forEach(prop => this.registerProperty(prop))
  }

  protected registerProperty(prop: string) {
    const target = this._content
    const getter = this._handler.get
    const setter = this._handler.set
    Object.defineProperty(this, prop, {
      configurable: true,
      enumerable: true,
      get: getter.bind(target, prop),
      set: setter.bind(target, prop),
    })
  }

  public syncOtherContents() {
    if (this._options?.copyOnWrite) {
      this._prevContent = null
      this._origContent = null
    } else {
      this.setPrevContent(this.content)
      this.setOrigContent(this.content)
    }
  }

}
